import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Breadcrumbs from "@dashboard/components/breadcrumbs/breadcrumbs";
import { UiButton, UiPhone } from "@domun/component-library";
import { DeliveryComplementaryEmptyState } from "../../components/delivery-complementary-empty-state/delivery-complementary-empty-state";
import {
  DeliveryHtmlHelpText,
  DeliveryProductsAlert,
  DeliverySizeHelpTexts,
} from "../../components/delivery-create-help/delivery-create-help";
import Truck from "@sumerkit/icons/truck";
import { DeliveryProviderSelector } from "../../components/delivery-provider-selector/delivery-provider-selector";
import { DeliveryDestinationLocation } from "../../components/delivery-destination-location/delivery-destination-location";
import { DeliveryAddressMapModal } from "../../components/delivery-address-map-modal/delivery-address-map-modal";
import { DeliveryPageWrapper } from "../../components/delivery-page-layout/delivery-page-layout.styled";
import { useDeliveryCreate } from "../../hooks/use-delivery-create.hook";
import { DELIVERY_ROUTES } from "../../delivery.routes";

const FieldLabel: FC<{ required?: boolean; children: ReactNode }> = ({
  required,
  children,
}) => (
  <label>
    {children}
    {required && (
      <span className="required-mark" aria-hidden="true">
        {" "}
        *
      </span>
    )}
  </label>
);

const DeliveryCreate: FC = () => {
  const { t } = useTranslation();
  const {
    linkedOrderId,
    isManualMode,
    origin,
    customer,
    updateCustomer,
    phoneOptions,
    handlePhoneChange,
    deliveryTypes,
    selectedDeliveryType,
    onDeliveryTypeChange,
    deliverySize,
    setDeliverySize,
    sizesForType,
    selectedDeliverySize,
    selectedProviderId,
    applyProviderSelection,
    providersForSelectedType,
    showProvidersEmptyState,
    handleEditOriginAddress,
    pickupDate,
    setPickupDate,
    showPickupDateField,
    hasQuoted,
    calculating,
    handleCalculate,
    handleContinue,
    canContinue,
    breadcrumbs,
    partner,
    partnerCountry,
    countryCities,
    defaultPhonePrefix,
    addressSelected,
    locationConfirmed,
    showMapModal,
    handleCityChange,
    handleAddressAutocomplete,
    handleAddressInputChange,
    handleAddressDetailsChange,
    openMapModal,
    closeMapModal,
    handleMapConfirm,
  } = useDeliveryCreate();

  const settingsPath = partner?.userId
    ? `/${partner.userId}/delivery/${DELIVERY_ROUTES.SETTINGS}`
    : "#";

  return (
    <DeliveryPageWrapper>
      <div className="header">
        <Breadcrumbs items={breadcrumbs} backButton />
      </div>
      <h2>
        {t("delivery.create.title")}
        {linkedOrderId ? ` #${linkedOrderId}` : ""}
      </h2>

      <div className="card">
        <h3>{t("delivery.create.origin")}</h3>
        <p>
          {origin.address}, {origin.city}
        </p>
      </div>

      <div className="card">
        <h3>{t("delivery.create.destination")}</h3>
        {isManualMode && (
          <div className="field">
            <FieldLabel required>{t("delivery.create.customerName")}</FieldLabel>
            <input
              value={customer.name || ""}
              onChange={(e) => updateCustomer("name", e.target.value)}
              required
            />
          </div>
        )}
        <div className="field phone-field">
          <UiPhone
            label={
              isManualMode
                ? `${t("delivery.create.customerPhone")} *`
                : t("delivery.create.customerPhone")
            }
            prefixValue={customer.prefixPhone || defaultPhonePrefix}
            phoneValue={customer.phone || ""}
            expandiblePosition="top"
            options={phoneOptions}
            onChange={handlePhoneChange}
          />
        </div>
        <div className="field">
          <label>{t("delivery.create.customerEmail")}</label>
          <input
            type="email"
            value={customer.email || ""}
            onChange={(e) => updateCustomer("email", e.target.value)}
          />
        </div>
        <DeliveryDestinationLocation
          customer={customer}
          country={partnerCountry}
          cities={countryCities}
          addressSelected={addressSelected}
          locationConfirmed={locationConfirmed}
          onCityChange={handleCityChange}
          onAddressAutocomplete={handleAddressAutocomplete}
          onAddressInputChange={handleAddressInputChange}
          onAddressDetailsChange={handleAddressDetailsChange}
          onAdjustOnMap={openMapModal}
        />
      </div>

      <div className="card">
        {deliveryTypes.length > 0 && (
          <div className="field">
            <FieldLabel required>{t("delivery.create.deliveryType")}</FieldLabel>
            <div className="delivery-type-tabs">
              {deliveryTypes
                .filter((type) => type.enabled)
                .map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={
                      selectedDeliveryType?.id === type.id
                        ? "delivery-type-tab active"
                        : "delivery-type-tab"
                    }
                    onClick={() => onDeliveryTypeChange(type)}
                  >
                    {type.label}
                  </button>
                ))}
            </div>
            {selectedDeliveryType?.helpText && (
              <DeliveryHtmlHelpText html={selectedDeliveryType.helpText} />
            )}
          </div>
        )}

        <div className="field">
          <FieldLabel required>{t("delivery.create.deliverySize")}</FieldLabel>
          {sizesForType.length === 0 ? (
            <p className="hint-text">
              {t("delivery.create.noSizes")}{" "}
              <Link to={settingsPath}>{t("delivery.create.goToSettings")}</Link>
            </p>
          ) : (
            <div className="delivery-field-block">
              <div className="delivery-size-alert">
                <DeliveryProductsAlert
                  message={t("delivery.create.productsAlertMessage")}
                />
              </div>
              <select
                value={deliverySize}
                onChange={(e) => setDeliverySize(e.target.value)}
              >
                <option value="">—</option>
                {sizesForType.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <DeliverySizeHelpTexts
                helpText1={selectedDeliverySize?.helpText1}
                helpText2={selectedDeliverySize?.helpText2}
              />
              {isManualMode && (
                <div className="field order-detail-field">
                  <label>{t("delivery.create.orderDetail")}</label>
                  <textarea
                    rows={3}
                    placeholder={t("delivery.create.orderDetailHint")}
                    value={customer.observations || ""}
                    onChange={(e) =>
                      updateCustomer("observations", e.target.value)
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {showPickupDateField && (
          <div className="field">
            <label>{t("delivery.create.pickupDate")}</label>
            <input
              type="date"
              value={pickupDate}
              onChange={(e) => setPickupDate(e.target.value)}
            />
          </div>
        )}

        <UiButton
          onClick={handleCalculate}
          isLoading={calculating}
          disabled={calculating}
        >
          {t("delivery.create.calculate")}
        </UiButton>

        {hasQuoted && (
          <div className="field">
            <FieldLabel required>{t("delivery.create.provider")}</FieldLabel>
            {showProvidersEmptyState ? (
              <DeliveryComplementaryEmptyState
                icon={<Truck weight="light" />}
                title={t("delivery.create.providersEmptyTitle")}
                description={t("delivery.create.providersEmptyMessage")}
                buttonLabel={t("delivery.create.providersEmptyButton")}
                onButtonPress={handleEditOriginAddress}
              />
            ) : (
              <DeliveryProviderSelector
                providers={providersForSelectedType}
                selectedProviderId={selectedProviderId}
                onSelect={applyProviderSelection}
                userCountry={partnerCountry}
              />
            )}
          </div>
        )}
      </div>

      <div className="actions">
        <UiButton onClick={handleContinue} disabled={!canContinue}>
          {t("delivery.create.continue")}
        </UiButton>
      </div>

      <DeliveryAddressMapModal
        show={showMapModal}
        onClose={closeMapModal}
        onConfirm={handleMapConfirm}
        initialAddress={customer.address || ""}
        initialCity={customer.city || ""}
        initialLat={customer.lat}
        initialLng={customer.lng}
        country={partnerCountry}
        originLat={origin.lat}
        originLng={origin.lng}
      />
    </DeliveryPageWrapper>
  );
};

export default DeliveryCreate;
